import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import "./Auth.css";
import { Button } from "./Button";
import { Alert } from "./Card";
import { Field, Input, Select } from "./Field";
import { Icon } from "./Icon";
import { Chip } from "./Status";
import { ScopePicker, ScopeText } from "./ProjectScope";
import type { ProjectName } from "./ProjectScope";
import { roleText, sessionText, ssoErrorText } from "../lib/auth";
import type { LoginMode, Me, Role, UserRow } from "../lib/auth";
import { hole, rich, tx } from "../i18n";
import { KEEP_LOGIN_DAYS, LoginRemember, readSavedLoginId } from "@mops/totp-ui-v2";

/* ══ 로그인의 부품 — 채택 시안 안 B(Figma 90-58) · 공통 ①②③④(91-2 · 91-85 · 92-2 · 92-215) ══
   전부 **값을 받아 그리기만** 한다 — 서버를 부르는 것은 화면(`app/AuthGate.tsx` ·
   `pages/AccountPage.tsx` · `pages/SettingsPage.tsx`)이다. 그래야 패턴 페이지가 상태마다
   그대로 세울 수 있다.

   실패는 **서버가 준 문장 그대로** 보여준다(공통 ②). 화면이 문장을 바꿔 쓰지 않는다. */

/** 로그인 전 화면의 바탕 — 셸(내비) 없이 가운데 카드 하나. 공통 v3 의 `.b-auth`. */
export function AuthFrame({ children }: { children: ReactNode }) {
  return <main className="b-auth">{children}</main>;
}

function AuthCard({ title, sub, children }: { title: string; sub: string; children: ReactNode }) {
  return (
    <section className="b-auth-card" aria-label={`${title} — ${sub}`}>
      <header className="authcard__head">
        <h1 className="b-auth-title">{title}</h1>
        <p className="b-auth-sub">{sub}</p>
      </header>
      {children}
    </section>
  );
}

/** 실패 상자 — 말머리(무엇이) + 서버 문장. 색은 셋째 신호다(× · ! 글리프 · 말머리 글자).
 *  `warning` 은 「다시 누르면 된다」 쪽(공통 ② 의 hub 에서 돌아오다 멈춤). */
function Fail({ head, tone = "error", children }: { head: string; tone?: "error" | "warning"; children: ReactNode }) {
  return (
    <div className={`b-alert ${tone === "warning" ? "b-alert-warning" : "b-alert-danger"}`} role="alert">
      <Icon name={tone === "warning" ? "alert" : "x"} />
      <div className="b-alert-body">
        <p className="b-alert-title">{head}</p>
        <p className="b-alert-text">{children}</p>
      </div>
    </div>
  );
}

/** 로그인 카드 — 채택 시안 안 B(Figma 90-58). 모드마다 모양이 셋이다:
 *  - `local`: 아이디·비밀번호 폼만(hub 가 없는 설치 — SSO 단추를 그리지 않는다).
 *  - `sso`: 「MOPS 계정으로 로그인 (hub)」 단추만. 로컬 문은 서버가 닫았다(403).
 *  - `local+sso`: hub 단추가 맨 위(2단계를 거치는 문이 앞에 선다), 이 설치의 계정은 **글자로 된
 *    한 줄로 접힌다** — 아이콘만 두면 「로컬 로그인이 없다」로 읽힌다(B 의 대가).
 *  `error` 는 서버 문장이다 — 틀렸든 막혔든 그 문장이 말한다. 비밀번호 칸만 비운다. */
export function LoginCard({
  mode = "local",
  busy,
  error,
  notice,
  ssoError,
  issuer = null,
  initialUser = "",
  startOpen,
  onSubmit,
  onHub,
}: {
  mode?: LoginMode;
  busy?: boolean;
  error?: string | null;
  /** 실패가 아닌 안내(세션 만료 등). */
  notice?: string | null;
  /** hub 에서 돌아오다 실패한 갈래(`sso_error`). */
  ssoError?: string | null;
  issuer?: string | null;
  initialUser?: string;
  /** `local+sso` 에서 접힌 폼을 펴 둔 채로 시작한다(패턴 페이지 · 로컬 실패 뒤). */
  startOpen?: boolean;
  /** 비밀번호 단계 — 체크 둘(아이디 저장 · 로그인 유지)도 같이 넘긴다. */
  onSubmit?: (username: string, password: string, opts: { saveId: boolean; remember: boolean }) => void;
  onHub?: () => void;
}) {
  // 저장해 둔 아이디가 있으면 「아이디 저장」은 켜진 채로 시작한다 — 지난번에 켜 둔 사람이다.
  const [username, setUsername] = useState(() => initialUser || readSavedLoginId() || "");
  const [saveId, setSaveId] = useState(() => readSavedLoginId() !== null);
  const [remember, setRemember] = useState(false);
  const [password, setPassword] = useState("");
  const [open, setOpen] = useState(Boolean(startOpen || error || initialUser));
  useEffect(() => {
    if (error) {
      setPassword("");
      setOpen(true);
    }
  }, [error]);
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit?.(username.trim(), password, { saveId, remember });
  }
  const sso = ssoError ? ssoErrorText(ssoError, issuer) : null;
  const form = (
    <form className="authform" onSubmit={submit}>
      <Field label={tx("field.username")}>
        {(id) => <Input id={id} autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required />}
      </Field>
      <Field label={tx("field.password")}>
        {(id, invalid) => (
          <Input
            id={id}
            type="password"
            autoComplete="current-password"
            value={password}
            invalid={invalid || Boolean(error)}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        )}
      </Field>
      <LoginRemember
        saveId={saveId}
        onSaveIdChange={setSaveId}
        keep={remember}
        onKeepChange={setRemember}
        disabled={busy}
        saveIdLabel={tx("auth.login.saveId")}
        keepLabel={tx("auth.login.remember", { days: KEEP_LOGIN_DAYS })}
      />
      <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!username.trim() || !password}>
        {tx("common.action.login")}
      </Button>
    </form>
  );
  const hubButton = (
    <Button variant="primary" size="lg" className="authcard__hub" onClick={onHub}>
      {tx("auth.login.hub")}
    </Button>
  );
  return (
    <AuthCard title="b3x-in" sub={tx("common.action.login")}>
      {notice && !error && !sso ? <Alert tone="info">{notice}</Alert> : null}
      {sso ? (
        <Fail head={sso.head} tone={sso.tone}>
          {sso.body}
        </Fail>
      ) : null}
      {error ? <Fail head={tx("auth.login.failed")}>{error}</Fail> : null}
      {mode === "local" ? (
        <>
          {form}
          <p className="authcard__note">{tx("auth.login.noteLocal")}</p>
        </>
      ) : mode === "sso" ? (
        <>
          <p className="b-auth-sub">{tx("auth.login.ssoOnly")}</p>
          {hubButton}
          <p className="authcard__note">{tx("auth.login.noteSso")}</p>
        </>
      ) : (
        <>
          {hubButton}
          <p className="authcard__note">{tx("auth.login.noteBoth")}</p>
          <div className="authcard__fold">
            <button type="button" className="authcard__link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
              {tx(open ? "auth.login.localOpen" : "auth.login.localClosed")}
            </button>
            {open ? form : <p className="authcard__note">{tx("auth.login.foldNote")}</p>}
          </div>
        </>
      )}
    </AuthCard>
  );
}

/** 첫 설정 — 계정이 0개이고 설치 토큰이 있을 때만 서버가 받는다(공통 ①). 비밀번호 확인이
 *  다른 것만 여기서 본다 — 서버에는 확인 칸이 없다(그 밖의 규칙은 서버 문장이 말한다). */
export function SetupCard({
  busy,
  error,
  onSubmit,
}: {
  busy?: boolean;
  error?: string | null;
  onSubmit?: (b: { token: string; username: string; password: string }) => void;
}) {
  const [token, setToken] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const mismatch = confirm.length > 0 && confirm !== password;
  function submit(e: FormEvent) {
    e.preventDefault();
    if (mismatch) return;
    onSubmit?.({ token: token.trim(), username: username.trim(), password });
  }
  return (
    <AuthCard title={tx("auth.setup.title")} sub={tx("auth.setup.sub")}>
      {error ? <Fail head={tx("auth.setup.failed")}>{error}</Fail> : null}
      <form className="authform" onSubmit={submit}>
        <Field label={tx("auth.setup.token")} hint={tx("auth.setup.tokenHint")}>
          {(id) => <Input id={id} autoComplete="off" spellCheck={false} value={token} onChange={(e) => setToken(e.target.value)} required />}
        </Field>
        <Field label={tx("field.username")}>
          {(id) => <Input id={id} autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required />}
        </Field>
        <Field label={tx("field.password")}>
          {(id) => (
            <Input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          )}
        </Field>
        <Field label={tx("auth.setup.confirm")} error={mismatch ? tx("auth.setup.mismatch") : undefined}>
          {(id, invalid) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={confirm}
              invalid={invalid}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!token.trim() || !username.trim() || !password || mismatch}>
          {tx("auth.setup.submit")}
        </Button>
      </form>
      <p className="authcard__note">{tx("auth.setup.note")}</p>
    </AuthCard>
  );
}

/** 내비 오른쪽 끝의 계정 단추 + 팝오버(공통 ③). 「내 계정」은 내비 링크를 늘리지 않고 여기서 연다. */
export function AccountMenu({ me, onLogout, forceOpen }: { me: Me; onLogout?: () => void; forceOpen?: boolean }) {
  const [open, setOpen] = useState(Boolean(forceOpen));
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open || forceOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, forceOpen]);
  return (
    <div className="b-menu-wrap" ref={wrap}>
      {/* 계정 단추는 아이콘만이다 — 이름 길이로 상단 바 오른쪽 폭이 흔들리지 않게 한다(공통 셸 본보기 ola-mops).
          누가 들어와 있는지는 메뉴를 열면 맨 위에 보인다. */}
      <button
        type="button"
        className="b-ibtn b-btn-ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={tx("account.button", { name: me.username })}
        title={tx("account.button", { name: me.username })}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="user" />
      </button>
      {open ? <AccountPanel me={me} onLogout={onLogout} onPick={() => setOpen(false)} /> : null}
    </div>
  );
}

/** 계정 메뉴 — 공통 `.b-menu`. 맨 위에 누구인지, 그 아래 갈 곳 · 로그아웃. */
export function AccountPanel({ me, onLogout, onPick, inline }: { me: Me; onLogout?: () => void; onPick?: () => void; inline?: boolean }) {
  return (
    <div className={`b-menu acctpanel${inline ? " acctpanel--inline" : ""}`} role="menu" aria-label={tx("account.menu")}>
      <div className="acctpanel__head">
        <b>{me.displayName ? `${me.displayName} (${me.username})` : me.username}</b>
        <span className="acctpanel__sub">
          {tx(me.via === "sso" ? "account.viaHub" : "account.viaLocal")} · {roleText(me.role)}
        </span>
      </div>
      <div className="b-menu-sep" />
      <a className="b-menu-item" role="menuitem" href="#/account" onClick={onPick}>
        {tx("account.title")}
      </a>
      <button type="button" className="b-menu-item" role="menuitem" onClick={onLogout}>
        {tx("common.action.logout")}
      </button>
      <div className="b-menu-sep" />
      <p className="acctpanel__note">{tx("account.logoutNote")}</p>
    </div>
  );
}

/** 계정 정보 카드(#/account 왼쪽 위). */
export function AccountInfo({ me }: { me: Me }) {
  return (
    <section className="b-card acctcard">
      <h2 className="b-card-title">{me.username}</h2>
      <dl className="kv acctcard__kv">
        <dt>{tx("field.name")}</dt>
        <dd>{me.displayName ?? tx("account.unnamed")}</dd>
        <dt>{tx("field.role")}</dt>
        <dd>{roleText(me.role)}</dd>
        <dt>{tx("account.session")}</dt>
        <dd>{sessionText(me)}</dd>
        <dt>{tx("users.col.totp")}</dt>
        <dd>{me.totpEnabled ? `✓ ${tx("account.totpOn")}` : me.via === "sso" ? tx("account.totpHub") : tx("account.totpOff")}</dd>
      </dl>
      <p className="authcard__note">{tx("account.sessionNote")}</p>
    </section>
  );
}

/** 비밀번호 바꾸기. 지금 비밀번호를 다시 받는다(열어 둔 화면에서 남이 못 바꾸게 — 서버가 본다).
 *  비밀번호가 없는 계정(hub 로만 들어온 사람)은 이 칸 대신 그 사실을 글로 말한다. */
export function PasswordCard({
  hasPassword,
  busy,
  error,
  done,
  onSubmit,
}: {
  hasPassword: boolean;
  busy?: boolean;
  error?: string | null;
  done?: boolean;
  onSubmit?: (current: string, next: string) => void;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const mismatch = confirm.length > 0 && confirm !== next;
  useEffect(() => {
    if (done) {
      setCurrent("");
      setNext("");
      setConfirm("");
    }
  }, [done]);
  if (!hasPassword) {
    return (
      <section className="b-card acctcard">
        <h2 className="b-card-title">{tx("account.password.title")}</h2>
        <p className="muted">{tx("account.password.none")}</p>
      </section>
    );
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    if (mismatch) return;
    onSubmit?.(current, next);
  }
  return (
    <section className="b-card acctcard">
      <h2 className="b-card-title">{tx("account.password.title")}</h2>
      {error ? <Alert tone="error">{error}</Alert> : null}
      {done ? <Alert tone="success">{tx("account.password.done")}</Alert> : null}
      <form className="authform" onSubmit={submit}>
        <Field label={tx("account.password.current")}>
          {(id) => (
            <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          )}
        </Field>
        <Field label={tx("account.password.next")}>
          {(id) => <Input id={id} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />}
        </Field>
        <Field label={tx("account.password.confirm")} error={mismatch ? tx("account.password.mismatch") : undefined}>
          {(id, invalid) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={confirm}
              invalid={invalid}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" loading={busy} disabled={!current || !next || mismatch}>
          {tx("account.password.submit")}
        </Button>
      </form>
    </section>
  );
}

/** 사용자 표(설정 › 사용자, 공통 ④). 줄을 펼치면 역할 · 막기가 그 자리에 열린다.
 *  지우는 단추는 없다 — 담당 · 이력이 그 이름을 들고 있어 **막는다**. */
export function UserTable({
  rows,
  meId,
  openId,
  busyId,
  error,
  onToggle,
  onUpdate,
  extra,
  projects = [],
  savedId,
}: {
  rows: UserRow[];
  meId?: string;
  openId?: string | null;
  busyId?: string | null;
  error?: string | null;
  onToggle?: (id: string) => void;
  onUpdate?: (id: string, b: UserPatch) => void;
  /** 범위 칸 · 고르기의 이름표. 못 읽었으면 빈 목록 — 범위 칸은 id 그대로 보인다. */
  projects?: ProjectName[];
  /** 방금 이메일 · 범위를 저장한 줄 — 그 자리에 「저장했습니다」. */
  savedId?: string | null;
  /** 펼친 줄에 더 세울 칸(2단계 해제 등) — 그 칸이 무엇을 부를지는 화면이 정한다. */
  extra?: (u: UserRow) => ReactNode;
}) {
  return (
    // 공통 격자 표(.b-dt.is-grid) — 행 아래에 폼을 펼쳐야 해서 <table> 로는 안 된다. 폰에서도 카드로 접지 않고
    // 틀(.b-table-wrap) 안에서 가로로 구른다(공통 규칙 — 관제 표는 열을 맞대 보는 것이 목적이다).
    <div className="b-table-wrap">
    <div className="b-dt is-grid utable" role="table" aria-label={tx("users.title")}>
      <div className="b-dt-head utable__cols" role="row">
        <span className="b-dt-cell" role="columnheader">{tx("field.username")}</span>
        <span className="b-dt-cell" role="columnheader">{tx("field.name")}</span>
        <span className="b-dt-cell" role="columnheader">{tx("users.col.email")}</span>
        <span className="b-dt-cell" role="columnheader">{tx("field.role")}</span>
        <span className="b-dt-cell" role="columnheader">{tx("users.col.scope")}</span>
        <span className="b-dt-cell" role="columnheader">{tx("users.col.door")}</span>
        <span className="b-dt-cell" role="columnheader">{tx("users.col.totp")}</span>
        <span className="b-dt-cell" role="columnheader">{tx("field.status")}</span>
        <span className="b-dt-cell" role="columnheader">{tx("users.col.actions")}</span>
      </div>
      {rows.map((u) => {
        const open = openId === u.id;
        return (
          <div className={`utable__group${open ? " utable__group--open" : ""}`} key={u.id}>
            <div className={`b-dt-row utable__cols${open ? " is-selected" : ""}`} role="row">
              <span className="b-dt-cell is-primary" role="cell">
                {u.username}
                {u.id === meId ? <span className="muted"> {tx("users.me")}</span> : null}
              </span>
              <span className="b-dt-cell" role="cell">{u.displayName ?? "—"}</span>
              <span className="b-dt-cell utable__email" role="cell" title={u.email ?? undefined}>{u.email ?? "—"}</span>
              <span className="b-dt-cell" role="cell">{roleText(u.role)}</span>
              <span className="b-dt-cell" role="cell">
                {u.role === "admin" ? <span className="muted">{tx("projectScope.all")}</span> : <ScopeText projects={u.projects} names={projects} />}
              </span>
              <span className="b-dt-cell" role="cell">{tx(!u.hasPassword ? "users.door.hubOnly" : u.hubLinked ? "users.door.both" : "users.door.local")}</span>
              <span className="b-dt-cell" role="cell">
                {u.totpEnabled ? (
                  <Chip tone="success">
                    <span aria-hidden="true">✓</span> {tx("account.totpOn")}
                  </Chip>
                ) : u.hasPassword ? (
                  <span className="muted">— {tx("account.totpOff")}</span>
                ) : (
                  <span className="muted">{tx("account.totpHub")}</span>
                )}
              </span>
              <span className="b-dt-cell" role="cell">
                {u.disabled ? (
                  <Chip tone="error">
                    <span aria-hidden="true">⊘</span> {tx("users.blocked")}
                  </Chip>
                ) : (
                  tx("common.state.active")
                )}
              </span>
              <span className="b-dt-cell" role="cell">
                <button type="button" className="utable__toggle" aria-expanded={open} onClick={() => onToggle?.(u.id)}>
                  {tx(open ? "users.fold" : "users.unfold")}
                </button>
              </span>
            </div>
            {open ? (
              <div className="utable__detail" role="row">
                <div className="utable__cell" role="cell">
                  <Field label={tx("field.role")}>
                    {(id) => (
                      <Select
                        id={id}
                        size="md"
                        value={u.role}
                        disabled={busyId === u.id}
                        onChange={(e) => onUpdate?.(u.id, { role: e.target.value as Role })}
                      >
                        <option value="user">{tx("role.user")}</option>
                        <option value="admin">{tx("common.role.admin")}</option>
                      </Select>
                    )}
                  </Field>
                  <Button
                    variant={u.disabled ? "secondary" : "danger"}
                    loading={busyId === u.id}
                    disabled={u.id === meId && !u.disabled}
                    title={u.id === meId && !u.disabled ? tx("users.selfBlock") : undefined}
                    onClick={() => onUpdate?.(u.id, { disabled: !u.disabled })}
                  >
                    {tx(u.disabled ? "users.unblock" : "users.block")}
                  </Button>
                  <p className="authcard__note">{tx("users.blockNote")}</p>
                </div>
                <UserProfileCell
                  u={u}
                  projects={projects}
                  busy={busyId === u.id}
                  saved={savedId === u.id}
                  onSave={(b) => onUpdate?.(u.id, b)}
                />
                <div className="utable__cell" role="cell">
                  <p className="utable__label">{tx("field.password")}</p>
                  <p className="authcard__note">
                    {rich(tx("users.passwordNote", hole("cmd")), { cmd: <code>make password-reset ACCOUNT={u.username}</code> })}
                  </p>
                </div>
                {extra?.(u)}
              </div>
            ) : null}
          </div>
        );
      })}
      {error ? (
        <div className="utable__error">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
    </div>
    </div>
  );
}

export type UserPatch = { role?: Role; disabled?: boolean; email?: string; projects?: string[] | null };

/** 펼친 줄의 이메일 · 범위 칸. 바뀐 칸만 보낸다 — 안 바꾼 칸은 보내지 않는다(서버는 「없음 = 그대로」).
 *  관리자는 범위를 안 보니 고르기를 잠그고 그렇다고 글자로 말한다. */
export function UserProfileCell({
  u,
  projects,
  busy,
  saved,
  onSave,
}: {
  u: UserRow;
  projects: ProjectName[];
  busy?: boolean;
  saved?: boolean;
  onSave?: (b: UserPatch) => void;
}) {
  const [email, setEmail] = useState(u.email ?? "");
  const [scope, setScope] = useState<string[] | null>(u.projects);
  useEffect(() => {
    setEmail(u.email ?? "");
    setScope(u.projects);
  }, [u]);
  const isAdmin = u.role === "admin";
  const b: UserPatch = {};
  if (email.trim() !== (u.email ?? "")) b.email = email.trim();
  if (!isAdmin && JSON.stringify(scope) !== JSON.stringify(u.projects)) b.projects = scope;
  const dirty = Object.keys(b).length > 0;
  return (
    <div className="utable__cell utable__profile" role="cell">
      <p className="utable__label">{tx("users.profile.title")}</p>
      <Field label={tx("users.profile.email")} hint={tx("users.profile.emailHint")}>
        {(id) => <Input id={id} type="email" size="md" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />}
      </Field>
      <ScopePicker name={`uscope-${u.id}`} value={isAdmin ? null : scope} projects={projects} onChange={setScope} disabled={isAdmin || busy} />
      <p className="authcard__note">{isAdmin ? tx("projectScope.adminAll") : tx("users.profile.scopeNote")}</p>
      <Button variant="primary" size="md" loading={busy} disabled={!dirty} onClick={() => onSave?.(b)}>
        {tx("users.profile.save")}
      </Button>
      {saved && !dirty ? <p className="authcard__note">✓ {tx("users.profile.saved")}</p> : null}
    </div>
  );
}

/** 새 계정(설정 › 사용자). 처음 비밀번호는 만든 사람이 본인에게 전하고, 본인이 「내 계정」에서 바꿉니다. */
export function NewUserForm({
  busy,
  error,
  doneName,
  onSubmit,
}: {
  busy?: boolean;
  error?: string | null;
  /** 방금 만든 아이디 — 있으면 칸을 비우고 그 사실을 말한다. */
  doneName?: string | null;
  onSubmit?: (b: { username: string; displayName?: string; role: Role; password: string; email?: string }) => void;
}) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [password, setPassword] = useState("");
  useEffect(() => {
    if (doneName) {
      setUsername("");
      setEmail("");
      setDisplayName("");
      setRole("user");
      setPassword("");
    }
  }, [doneName]);
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit?.({ username: username.trim(), displayName: displayName.trim() || undefined, role, password, email: email.trim() || undefined });
  }
  return (
    <section className="b-card acctcard">
      <h2 className="b-card-title">{tx("users.new.title")}</h2>
      {error ? <Alert tone="error">{error}</Alert> : null}
      {doneName ? <Alert tone="success">{tx("users.new.done", { name: doneName })}</Alert> : null}
      <form className="authform authform--grid" onSubmit={submit}>
        <Field label={tx("field.username")}>
          {(id) => <Input id={id} autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} required />}
        </Field>
        <Field label={tx("field.name")}>
          {(id) => <Input id={id} autoComplete="off" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />}
        </Field>
        <Field label={tx("field.role")}>
          {(id) => (
            <Select id={id} value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="user">{tx("role.user")}</option>
              <option value="admin">{tx("common.role.admin")}</option>
            </Select>
          )}
        </Field>
        <Field label={tx("users.new.email")}>
          {(id) => <Input id={id} type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label={tx("users.new.password")}>
          {(id) => (
            <Input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          )}
        </Field>
        <div className="authform__actions">
          <Button type="submit" variant="primary" loading={busy} disabled={!username.trim() || !password}>
            {tx("common.action.create")}
          </Button>
        </div>
      </form>
      <p className="authcard__note">{tx("users.new.note")}</p>
    </section>
  );
}

/** 개인 토큰 — pr.b3x.app 「내 계정 › 개인 토큰 등록」(claude · codex · figma · github)의 자리(t-in-port-admin-me).
 *  b3x-in 은 **사람마다 토큰을 두지 않는다** — 모델은 ola-mops 연결 토큰이 고르고, git 은 연결마다 링크 승인으로 받고,
 *  figma 는 설계 트랙 애드온의 설치 값이다. 칸을 지어내지 않고, 어디서 바꾸는지만 가리킨다(관리자만 그 문을 연다). */
export function TokenWhereCard({ isAdmin }: { isAdmin: boolean }) {
  const rows: [string, string, string][] = [
    [tx("account.tokens.model"), tx("account.tokens.modelWhere"), "#/conn"],
    [tx("account.tokens.git"), tx("account.tokens.gitWhere"), "#/git"],
    [tx("account.tokens.figma"), tx("account.tokens.figmaWhere"), ""],
  ];
  return (
    <section className="b-card acctcard">
      <h2 className="b-card-title">{tx("account.tokens.title")}</h2>
      <p className="authcard__note">{tx("account.tokens.lead")}</p>
      <dl className="kv acctcard__kv">
        {rows.map(([k, v, href]) => (
          <div key={k} className="acctcard__row">
            <dt>{k}</dt>
            <dd>
              {v}
              {href && isAdmin ? (
                <>
                  {" "}
                  <a href={href}>{tx("account.tokens.open")} →</a>
                </>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
      {!isAdmin ? <p className="authcard__note">{tx("account.tokens.adminOnly")}</p> : null}
    </section>
  );
}
